// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
import React from 'react';
import { Progress } from './ui/progress';
import { useI18n } from '../i18n';


const ProgressBar = ({ progress = 0, currentStep = '', substep = '', substepsBuffer = [] }) => {
  const { t } = useI18n();
  const STEPS = [
  { num: 1, label: t('step_1'), range: [0, 5] },
  { num: 2, label: t('step_2'), range: [5, 25] },
  { num: 3, label: t('step_3'), range: [25, 30] },
  { num: 4, label: t('step_4'), range: [30, 35] },
  { num: 5, label: t('step_5'), range: [35, 40] },
  { num: 6, label: t('step_6'), range: [40, 45] },
  { num: 7, label: t('step_7'), range: [45, 50] },
  { num: 8, label: t('step_8'), range: [50, 65] },
  { num: 9, label: t('step_9'), range: [65, 80] },
  { num: 10, label: t('step_10'), range: [80, 90] },
  { num: 11, label: t('step_11'), range: [90, 95] },
  { num: 12, label: t('step_12'), range: [95, 100] },
];

  const getCurrentStepIndex = () => {
    const normalizedProgress = Math.max(0, Math.min(100, progress));
    
    for (let i = 0; i < STEPS.length; i++) {
      const step = STEPS[i];
      const [start, end] = step.range;
      
      if (i === STEPS.length - 1) {
        if (normalizedProgress >= start && normalizedProgress <= end) {
          return i;
        }
      } else {
        if (normalizedProgress >= start && normalizedProgress < end) {
          return i;
        }
      }
    }

    return normalizedProgress >= 100 ? STEPS.length - 1 : 0;
  };

  const currentStepIndex = getCurrentStepIndex();

  return (
    <div className="space-y-4">
      {/* Barre de progression globale */}
      <div className="space-y-2">
        <Progress value={Math.min(100, progress)} />

        <div className="flex justify-between items-center">
          <span 
            className="text-sm font-semibold"
            style={{ color: 'var(--black)' }}
          >
            {currentStep || t('step_in_progress')}
          </span>
          <span 
            className="text-sm font-bold"
            style={{ color: 'var(--blue)' }}
          >
            {Math.min(100, progress).toFixed(1)}%
          </span>
        </div>
        
        {/* Substep actuel */}
        {substep && (
          <div className="pl-4 py-1">
            <div 
              className="text-sm font-medium flex items-center gap-2"
              style={{ color: 'var(--blue-light)' }}
            >
              <span className="animate-pulse">⚙️</span>
              <span>{substep}</span>
            </div>
          </div>
        )}
      </div>

      {/* Grille des 12 étapes */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
        {STEPS.map((step, index) => {
          const isCompleted = progress >= step.range[1] || (index === STEPS.length - 1 && progress >= 100);
          const isCurrent = index === currentStepIndex;
          const isPending = progress < step.range[0];

          return (
            <div
              key={step.num}
              className="p-3 rounded-lg text-center transition-all duration-200 shadow-sm"
              style={{
                background: isCompleted 
                  ? 'var(--gold)' 
                  : isCurrent 
                  ? 'var(--blue-light)' 
                  : 'var(--white)',
                color: isCompleted || isCurrent ? 'white' : 'var(--black)',
                border: isCompleted || isCurrent 
                  ? 'none' 
                  : '1px solid var(--gray)',
                opacity: isPending ? 0.5 : 1
              }}
            >
              <div className="text-xs font-bold mb-1">#{step.num}</div>
              <div className="text-xs truncate">{step.label}</div>
              {isCompleted && <div className="text-lg mt-1">✓</div>}
              {isCurrent && <div className="text-lg mt-1">⚙️</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProgressBar;